import { useCallback, useEffect, useState } from "react";
import { Toast } from "primereact/toast";

import useToastActionWrapper from "./useToastActionWrapper";
import {
  createWallbox,
  deleteWallbox,
  listWallbox,
  updateWallbox,
} from "../services/onboarding/wallbox";
import { WallboxEntry, WallboxEroundMappingInput } from "../types/onboarding";
import { wallboxCatalogueErrorMessage } from "../pages/Onboarding/WallboxCatalogue/components/wallboxCatalogueErrors";

interface WallboxFormValues {
  manufacturer: string;
  model_string: string;
  integrated: boolean;
  eround?: WallboxEroundMappingInput | null;
}

interface UseWallboxCatalogue {
  entries: WallboxEntry[];
  loading: boolean;
  error: boolean;
  reload: () => Promise<void>;
  create: (values: WallboxFormValues) => Promise<boolean>;
  update: (id: string, values: WallboxFormValues) => Promise<boolean>;
  remove: (id: string) => Promise<void>;
}

const useWallboxCatalogue = (
  toastRef: React.RefObject<Toast>
): UseWallboxCatalogue => {
  const { toastActionWrapper } = useToastActionWrapper(toastRef);
  const [entries, setEntries] = useState<WallboxEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await listWallbox();
      setEntries(data?.entries ?? []);
    } catch (e) {
      console.error("Failed to load wallbox catalogue", e);
      setError(true);
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load the wallbox catalogue.",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [toastRef]);

  useEffect(() => {
    reload();
  }, [reload]);

  // create/update return a boolean so the dialog can stay open on validation
  // failure; they still surface success/error toasts.
  const create = useCallback(
    async (values: WallboxFormValues): Promise<boolean> => {
      let ok = false;
      await toastActionWrapper({
        action: async () => {
          await createWallbox(values);
          await reload();
          ok = true;
          return true;
        },
        successMessage: "Catalogue entry added.",
        errorMessage: (err) =>
          wallboxCatalogueErrorMessage(err, "Could not add the catalogue entry."),
      });
      return ok;
    },
    [reload, toastActionWrapper]
  );

  const update = useCallback(
    async (id: string, values: WallboxFormValues): Promise<boolean> => {
      let ok = false;
      await toastActionWrapper({
        action: async () => {
          await updateWallbox(id, values);
          await reload();
          ok = true;
          return true;
        },
        successMessage: "Catalogue entry updated.",
        errorMessage: (err) =>
          wallboxCatalogueErrorMessage(err, "Could not update the catalogue entry."),
      });
      return ok;
    },
    [reload, toastActionWrapper]
  );

  const remove = useCallback(
    async (id: string) => {
      await toastActionWrapper({
        action: async () => {
          await deleteWallbox(id);
          await reload();
          return true;
        },
        successMessage: "Catalogue entry deleted.",
        errorMessage: "Could not delete the catalogue entry.",
      });
    },
    [reload, toastActionWrapper]
  );

  return { entries, loading, error, reload, create, update, remove };
};

export default useWallboxCatalogue;
